import React, { useEffect } from 'react';

import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';

import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

import type { TagSchedule, UpdateTagScheduleInput } from '~/services/TagsService';

import { useUpdateTagSchedule } from '../hooks';

import type { TagScheduleFormData } from './TagScheduleForm';
import TagScheduleForm from './TagScheduleForm';

interface TagScheduleEditProps {
  tagId?: string;
  open: boolean;
  scheduleToEdit: TagSchedule;
  onClose: () => void;
  onSave: (schedule: TagSchedule) => void;
}

export default function TagScheduleEdit({ open, tagId, scheduleToEdit, onClose, onSave }: TagScheduleEditProps) {
  const { enqueueSnackbar } = useSnackbar();
  const updateTagSchedule = useUpdateTagSchedule();

  const { control, handleSubmit, reset } = useForm<TagScheduleFormData>({
    defaultValues: {
      brands: [],
      regions: [],
      activeStartDate: null,
      activeEndDate: null,
      travelStartDate: null,
      travelEndDate: null,
    },
  });

  useEffect(() => {
    if (open && scheduleToEdit) {
      const activeStartDate =
        'activeStartDate' in scheduleToEdit && scheduleToEdit.activeStartDate ? scheduleToEdit.activeStartDate : null;
      const activeEndDate =
        'activeEndDate' in scheduleToEdit && scheduleToEdit.activeEndDate ? scheduleToEdit.activeEndDate : null;
      const travelStartDate =
        'travelStartDate' in scheduleToEdit && scheduleToEdit.travelStartDate ? scheduleToEdit.travelStartDate : null;
      const travelEndDate =
        'travelEndDate' in scheduleToEdit && scheduleToEdit.travelEndDate ? scheduleToEdit.travelEndDate : null;
      const regions = 'regions' in scheduleToEdit && scheduleToEdit.regions ? scheduleToEdit.regions : [];

      reset({
        brands: scheduleToEdit.brands,
        regions,
        activeStartDate,
        activeEndDate,
        travelStartDate,
        travelEndDate,
      });
    }
  }, [open, scheduleToEdit, reset]);

  const onSubmit = (data: TagScheduleFormData) => {
    if (!data.activeStartDate || !data.activeEndDate) {
      enqueueSnackbar('Please select both active period start and end dates', { variant: 'error' });
      return;
    }

    if (data.activeStartDate && data.activeEndDate && data.activeStartDate > data.activeEndDate) {
      enqueueSnackbar('Active period start date must be before end date', { variant: 'error' });
      return;
    }

    if (data.travelStartDate && data.travelEndDate && data.travelStartDate > data.travelEndDate) {
      enqueueSnackbar('Travel period start date must be before end date', { variant: 'error' });
      return;
    }

    if (data.regions.length === 0) {
      enqueueSnackbar('Please select at least one region', { variant: 'error' });
      return;
    }

    if (!tagId) {
      const updatedSchedule: TagSchedule = {
        ...scheduleToEdit,
        brands: data.brands,
        regions: data.regions,
        activeStartDate: data.activeStartDate,
        activeEndDate: data.activeEndDate,
        travelStartDate: data.travelStartDate || undefined,
        travelEndDate: data.travelEndDate || undefined,
      };
      onSave(updatedSchedule);
      onClose();
      return;
    }

    if (tagId && 'tagId' in scheduleToEdit) {
      const updateData: UpdateTagScheduleInput = {
        brands: data.brands,
        regions: data.regions,
        activeStartDate: data.activeStartDate,
        activeEndDate: data.activeEndDate,
        travelStartDate: data.travelStartDate || undefined,
        travelEndDate: data.travelEndDate || undefined,
      };
      updateTagSchedule.mutate(
        { id: scheduleToEdit.id, data: updateData },
        {
          onSuccess: () => onClose(),
        },
      );
    }
  };

  return (
    <Dialog open={open} onClose={updateTagSchedule.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Schedule</DialogTitle>
      <DialogContent>
        <TagScheduleForm control={control} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text" disabled={updateTagSchedule.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          autoFocus
          disabled={updateTagSchedule.isPending}
          startIcon={updateTagSchedule.isPending ? <CircularProgress size={16} /> : null}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
}
